import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, authHeaders, authHeadersFormData } from './apiHelpers';
import { MARKET } from '../constants/storageKeys';

export interface Document {
    documentId: number;
    filename: string;
    title: string;
    description: string;
    documentUrl: string;
    createdDate: Date;
    lastUpdatedBy: string;
    updateDateUTC: Date;
    sortOrder: number;
    carMake?: string;
    documentType?: string;
}

export interface DocumentViewModel {
    documentId: number;
    filename: string;
    title: string;
    description: string;
    carMake?: string;
    documentType?: string;
}

export function useDocuments(market?: string) {
    const resolvedMarket = market ?? window.localStorage.getItem(MARKET) ?? 'se';
    return useQuery<Document[], Error>({
        queryKey: ['documents', resolvedMarket],
        queryFn: () => apiFetch<Document[]>(`api/Documents?market=${encodeURIComponent(resolvedMarket)}`, {
            method: 'GET',
            headers: authHeaders()
        })
    });
}

export function useDocument(documentId: number) {
    return useQuery<Document, Error>({
        queryKey: ['document', documentId],
        queryFn: () => apiFetch<Document>(`api/Documents/GetDocument?documentId=${documentId}`, {
            method: 'GET',
            headers: authHeaders()
        }),
        enabled: documentId > 0
    });
}

export function useAddDocument() {
    const queryClient = useQueryClient();
    const market = window.localStorage.getItem(MARKET) ?? 'se';
    return useMutation<Document, Error, FormData>({
        mutationFn: (data) => apiFetch<Document>(`api/Documents/Add?market=${encodeURIComponent(market)}`, {
            method: 'POST',
            body: data,
            headers: authHeadersFormData()
        }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documents'] })
    });
}

export function useEditDocument() {
    const queryClient = useQueryClient();
    const market = window.localStorage.getItem(MARKET) ?? 'se';
    return useMutation<Document, Error, FormData>({
        mutationFn: (data) => apiFetch<Document>(`api/Documents/Update?market=${encodeURIComponent(market)}`, {
            method: 'POST',
            body: data,
            headers: authHeadersFormData()
        }),
        onSuccess: (doc) => {
            queryClient.invalidateQueries({ queryKey: ['documents'] });
            queryClient.invalidateQueries({ queryKey: ['document', doc.documentId] });
        }
    });
}

export function useDeleteDocument() {
    const queryClient = useQueryClient();
    return useMutation<void, Error, number>({
        mutationFn: (documentId) => apiFetch<void>(`api/Documents/Delete?documentId=${documentId}`, {
            method: 'DELETE',
            headers: authHeaders()
        }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documents'] })
    });
}

export function useSetDocumentSortOrder() {
    const queryClient = useQueryClient();
    return useMutation<void, Error, Document[]>({
        mutationFn: (sortOrders) => apiFetch<void>('api/Documents/SetSortOrder', {
            method: 'POST',
            body: JSON.stringify(sortOrders),
            headers: authHeaders()
        }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documents'] })
    });
}
